import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';

import { CameraPreview } from '@capacitor-community/camera-preview';
import { AlertController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { isEmpty } from 'lodash-es';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  race,
  shareReplay,
  take,
  tap,
  timer
} from 'rxjs';

import { AddressEnum } from '../../enums/address';
import { PrimarySubscriptionPlanAccessAliasEnum } from '../../enums/monetization';
import { ProfileSearchEnum } from '../../enums/profile-search';
import { GenericComponent } from '../../misc/components/generic/generic.component';
import { AddressPipe } from '../../misc/pipes/address.pipe';
import { CompassService } from '../../misc/services/compass.service';
import { MonetizationService } from '../../misc/services/monetization.service';
import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import * as fromSettingSelectors from '../../store/selectors/setting.selectors';
import { ProjectionData } from '../../typings/compass';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-property-search-camera',
  standalone: false,
  styleUrls: ['./camera.page.scss'],
  templateUrl: './camera.page.html'
})
export class CameraPageComponent extends GenericComponent {
  private addressPipe = inject(AddressPipe);
  private alertController = inject(AlertController);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private compassService = inject(CompassService);
  private mapGeocoder = inject(MapGeocoder);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  canStartCamera = true;
  cameraStartError: string | null = null;

  projectedLocation$: BehaviorSubject<ProjectionData | null> = new BehaviorSubject<ProjectionData | null>(null);

  debugMode$: Observable<boolean>;

  constructor() {
    super();

    this.debugMode$ = this.store.pipe(select(fromSettingSelectors.selectIsDebugModeEnabled));
  }

  async ionViewDidEnter() {
    const projectionData$ = await this.compassService.start();
    let isAccuracyAlertPresent = false;

    const isReady$ = projectionData$.pipe(
      map((data) => !!data?.isAccuracyReady),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.addUniqueSubscription(
      'accuracyCheck',
      race(
        isReady$.pipe(
          filter((isReady) => isReady),
          take(1)
        ),
        timer(30000)
      )
        .pipe(
          take(1),
          filter((value) => value === 0),
          tap(async () => {
            if (!isAccuracyAlertPresent) {
              isAccuracyAlertPresent = true;
              const alert = await this.alertController.create({
                backdropDismiss: false,
                buttons: [{ role: 'cancel', text: 'Ok' }],
                header: 'Feature Unavailable',
                message: `We are sorry, Camera View is not available on your device.`
              });
              await alert.present();
            }
          })
        )
        .subscribe()
    );

    this.addUniqueSubscription(
      'projectedLocation',
      projectionData$.subscribe((data) => {
        this.projectedLocation$.next(data);
      })
    );

    this.canStartCamera = true;
    this.cameraStartError = null;
    await this.stopCamera();
    await this.startCamera();
  }

  override async ionViewDidLeave() {
    super.ionViewDidLeave();
    await this.stopCamera();
    await this.compassService.stop();
  }

  async startCamera() {
    try {
      await CameraPreview.start({
        disableAudio: true,
        paddingBottom: 0,
        parent: 'cameraPreview',
        position: 'rear',
        toBack: true
      });
    } catch (error) {
      this.canStartCamera = false;
      this.cameraStartError = (error as Error).message;
    }

    this.changeDetectorRef.detectChanges();
  }

  async stopCamera() {
    try {
      await CameraPreview.stop();
    } catch {
      /* empty */
    }
  }

  async searchProfile() {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      this.mapGeocoder
        .geocode({
          location: this.projectedLocation$.getValue()?.projectedLocation
        })
        .subscribe(async ({ results, status }) => {
          if (status === google.maps.GeocoderStatus.OK && !isEmpty(results)) {
            this.store.dispatch(
              fromPropertyProfileActions.loadProfileByAddress({
                payload: {
                  address: this.addressPipe.transform(results[0].address_components, AddressEnum.LineOne),
                  searchType: ProfileSearchEnum.PropertySearch,
                  zip: this.addressPipe.transform(results[0].address_components, AddressEnum.ZipFull)
                }
              })
            );
          } else {
            const alert = await this.alertController.create({
              backdropDismiss: false,
              buttons: [
                {
                  role: 'cancel',
                  text: 'Ok'
                }
              ],
              header: 'Geocoding Failure',
              message: `We are sorry, we failed to determine the address of your coordinates.`
            });
            await alert.present();
          }
        });
    }
  }
}
